import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import type { ButtonProps } from 'antd';
import { useMutation } from '@apollo/client';
import MeterDetailsBlade from '../MeterDetailsBlade';
import BladeSection from '../BladeSection';
import bladeStyles from '../Blade.module.css';
import styles from './MiscChargeBlade.module.css';
import { SAVE_MISC_CHARGE, INVOICES_BY_VENDOR } from 'graphql/invoices.gql';
import { VendorMiscCharge } from 'types/invoices';

const { useModal } = Modal;

interface MiscChargeBladeProps {
  open: boolean;
  onClose: () => void;
  vendorName: string;
  vendorId: number;
  invoiceMonth: string;
  existingCharge: VendorMiscCharge | null | undefined;
  isApproved?: boolean;
}

const MiscChargeBlade = ({
  open,
  onClose,
  vendorName,
  vendorId,
  invoiceMonth,
  existingCharge,
  isApproved = false,
}: MiscChargeBladeProps) => {
  const [modal, contextHolder] = useModal();
  const [mounted, setMounted] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [hstApplicable, setHstApplicable] = useState(false);
  const [reasonError, setReasonError] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [saveMiscCharge] = useMutation(SAVE_MISC_CHARGE, {
    refetchQueries: [{ query: INVOICES_BY_VENDOR, variables: { monthYear: invoiceMonth } }],
  });

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      setMounted(true);
      setReason(existingCharge?.reason ?? '');
      setAmount(existingCharge?.amount != null ? String(existingCharge.amount) : '');
      setHstApplicable(existingCharge?.hst_applicable ?? false);
      setReasonError(false);
      setAmountError(false);
      setInlineError(null);
    } else {
      closeTimerRef.current = setTimeout(() => setMounted(false), 350);
    }
  }, [open, existingCharge]);

  if (!mounted) return null;

  const allEmpty = () => !reason.trim() && !amount.trim() && !hstApplicable;

  const validate = (): boolean => {
    let valid = true;
    if (!reason.trim()) { setReasonError(true); valid = false; }
    if (!amount.trim() || isNaN(Number(amount))) { setAmountError(true); valid = false; }
    return valid;
  };

  const handleApply = async () => {
    if (allEmpty()) {
      modal.confirm({
        title: 'No Miscellaneous Charge',
        content: 'No miscellaneous charge will be applied to this invoice. Do you want to continue?',
        okText: 'Yes',
        cancelText: 'No',
        okButtonProps: { 'data-testid': 'modal-ok-button' } as ButtonProps,
        cancelButtonProps: { 'data-testid': 'modal-cancel-button' } as ButtonProps,
        zIndex: 10002,
        onOk: () => existingCharge ? doSave(true) : onClose(),
      });
      return;
    }

    if (!validate()) return;
    await doSave(false);
  };

  const doSave = async (clearCharge: boolean) => {
    setSaving(true);
    setInlineError(null);
    try {
      await saveMiscCharge({
        variables: {
          ippId: vendorId,
          invoiceMonth,
          reason: clearCharge ? undefined : reason.trim(),
          amount: clearCharge ? undefined : Number(amount),
          hstApplicable: clearCharge ? undefined : hstApplicable,
          clearCharge,
        },
      });
      onClose();
    } catch (err: unknown) {
      setInlineError(err instanceof Error ? err.message : String(err) || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const infoSection = (
    <div className={bladeStyles.section}>
      <div className={bladeStyles.meterInfo}>
        <div>
          <div className={bladeStyles.fieldLabel}>Vendor</div>
          <div className={bladeStyles.fieldValue} data-testid="misc-vendor-name">{vendorName}</div>
        </div>
        <div>
          <div className={bladeStyles.fieldLabel}>Invoice Month</div>
          <div className={bladeStyles.fieldValue} data-testid="misc-invoice-month">{invoiceMonth}</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
    {contextHolder}
    <MeterDetailsBlade
      open={open}
      title="Miscellaneous Charge"
      onClose={onClose}
      monthLabel={invoiceMonth}
      handleApply={handleApply}
      applyLabel={existingCharge ? 'Update Charge' : 'Save Charge'}
      saving={saving}
      isDirty={!isApproved}
      inlineError={inlineError}
      infoSection={infoSection}
    >
      <BladeSection>
        {isApproved && (
          <div className={styles.approvedNote} data-testid="misc-locked-note">Invoice is approved — fields are locked.</div>
        )}
        <div className={styles.field}>
          <label className={styles.label}>
            Reason {!isApproved && <span className={styles.required}>*</span>}
          </label>
          <textarea
            className={`${styles.textarea} ${reasonError ? styles.error : ''}`}
            value={reason}
            maxLength={250}
            placeholder="Describe the miscellaneous charge"
            disabled={isApproved}
            onChange={(e) => { setReason(e.target.value); setReasonError(false); }}
            data-testid="misc-reason-input"
          />
          <span className={styles.charCount} data-testid="misc-reason-count">{reason.length} / 250</span>
          {reasonError && <span className={styles.errorText} data-testid="misc-reason-error">Reason is required</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            Amount ($) {!isApproved && <span className={styles.required}>*</span>}
          </label>
          <div className={styles.amountWrapper}>
            <span className={styles.currencyPrefix}>$</span>
            <input
              type="number"
              className={`${styles.amountInput} ${amountError ? styles.error : ''}`}
              value={amount}
              placeholder="0.00"
              step="0.01"
              min="0"
              disabled={isApproved}
              onChange={(e) => { setAmount(e.target.value); setAmountError(false); }}
              data-testid="misc-amount-input"
            />
          </div>
          {amountError && <span className={styles.errorText} data-testid="misc-amount-error">A valid amount is required</span>}
        </div>

        <div className={styles.checkboxRow}>
          <input
            id="misc-hst"
            type="checkbox"
            checked={hstApplicable}
            disabled={isApproved}
            onChange={(e) => setHstApplicable(e.target.checked)}
            data-testid="misc-hst-checkbox"
          />
          <label htmlFor="misc-hst" className={styles.checkboxLabel}>HST Applicable</label>
        </div>
      </BladeSection>
    </MeterDetailsBlade>
    </>
  );
};

export default MiscChargeBlade;
